import { LicenseStatus, Tenant, TenantId } from "@allbesmart/web-app-common";
import { ChangeDetectorRef, Component, OnDestroy } from "@angular/core";
import { Subscription } from "rxjs";
import { TenantSortOption } from "src/app/enums/tenant-sort-option";
import { OaiboxTelemetryService } from "src/app/services/oaibox-telemetry.service";
import { TenantManagementService } from "src/app/services/tenant-management.service";
import { ReactiveFormsModule, FormsModule } from "@angular/forms";
import { LicenseClientRowComponent } from "../../components/license-client-row/license-client-row.component";

@Component({
    selector: "oaibox-licenses-page",
    templateUrl: "./licenses-page.component.html",
    styleUrl: "./licenses-page.component.css",
    imports: [
        ReactiveFormsModule,
        FormsModule,
        LicenseClientRowComponent,
    ],
})
export class LicensesPageComponent implements OnDestroy {
  protected readonly AVAILABLE_LICENSE_STATUS = Object.values(LicenseStatus);
  private readonly LICENSE_STATUS_FILTER_LOCAL_STORAGE_ITEM_KEY =
    "537cd297-8ac0-407e-838e-8bca0043c71a";
  protected licenseStatusOption?: LicenseStatus;

  protected readonly AVAILABLE_SORT_OPTIONS = Object.values(TenantSortOption);
  private readonly SORT_OPTION_LOCAL_STORAGE_ITEM_KEY =
    "9a12f3e0-70c1-4b1a-9c1e-88a4f1d032aa";
  protected sortOption: TenantSortOption = TenantSortOption.NAME;

  private tenants: Map<TenantId, Tenant> = new Map();
  protected shownTenants: Tenant[] = [];

  protected keyword: string = "";

  private oaiboxStatusSubscription: Subscription;
  private tenantsSubscription?: Subscription;

  constructor(
    oaiboxTelemetryService: OaiboxTelemetryService,
    tenantManagementService: TenantManagementService,
    ch: ChangeDetectorRef
  ) {
    const licenseStatusOption = window.localStorage.getItem(
      this.LICENSE_STATUS_FILTER_LOCAL_STORAGE_ITEM_KEY
    );
    this.licenseStatusOption =
      (licenseStatusOption as LicenseStatus) || undefined;

    const sortOption = window.localStorage.getItem(
      this.SORT_OPTION_LOCAL_STORAGE_ITEM_KEY
    );
    this.sortOption = (sortOption as TenantSortOption) || TenantSortOption.NAME;

    this.oaiboxStatusSubscription = oaiboxTelemetryService
      .getOaiboxStatusChanges$()
      .subscribe(() => {
        this.handleTenantsChanges(tenantManagementService.getTenants());
        ch.markForCheck();
      });

    this.tenantsSubscription = tenantManagementService
      .getTenants$()
      .subscribe((tenants) => {
        this.handleTenantsChanges(tenants);
        ch.markForCheck();
      });

    tenantManagementService.refreshTenants();
  }

  private handleTenantsChanges(tenants: Tenant[]) {
    tenants.forEach((tenant) => {
      if (!this.tenants.has(tenant.id)) {
        this.tenants.set(tenant.id, tenant);
      }
    });

    // Remove missing tenants
    this.tenants.forEach((tenant, tenantId) => {
      if (!tenants.includes(tenant)) {
        this.tenants.delete(tenantId);
      }
    });

    // Sort and filter tenants
    this.filterMatchingTenants();
  }

  protected filterMatchingTenants(): void {
    let filteredTenants = [...this.tenants.values()];

    // Filter tenants by license status
    if (this.licenseStatusOption) {
      filteredTenants = filteredTenants.filter(
        (tenant) => tenant.license?.status === this.licenseStatusOption
      );
    }
    this.shownTenants = filteredTenants;

    // Filter by keywords
    if (this.keyword !== "") this.filterTenants();

    this.sortTenants();
  }

  private filterTenants(): void {
    const filterFn = (tenant: Tenant) => {
      const keywordLower = this.keyword.toLowerCase();
      return (
        tenant.clientName.toLowerCase().includes(keywordLower) ||
        tenant.clientDescription?.toLowerCase().includes(keywordLower) ||
        tenant.id?.toLowerCase().includes(keywordLower) ||
        tenant.clientDomain.toLowerCase().includes(keywordLower) ||
        tenant.registeredMachines.some((m) =>
          m.oaiboxSerialNumber?.toLowerCase().includes(keywordLower)
        )
      );
    };
    this.shownTenants = this.shownTenants.filter(filterFn);
  }

  private sortTenants(): void {
    this.shownTenants.sort(this.getSortFn());
  }

  private getSortFn(): (tenant1: Tenant, tenant2: Tenant) => number {
    switch (this.sortOption) {
      case TenantSortOption.START_DATE_CLOSEST:
        return (tenant1, tenant2) =>
          this.compareTimestamps(
            tenant1.license?.startTimestamp,
            tenant2.license?.startTimestamp,
            false
          );
      case TenantSortOption.START_DATE_FURTHEST:
        return (tenant1, tenant2) =>
          this.compareTimestamps(
            tenant1.license?.startTimestamp,
            tenant2.license?.startTimestamp,
            true
          );
      case TenantSortOption.END_DATE_CLOSEST:
        return (tenant1, tenant2) =>
          this.compareTimestamps(
            tenant1.license?.endTimestamp,
            tenant2.license?.endTimestamp,
            true
          );
      case TenantSortOption.END_DATE_FURTHEST:
        return (tenant1, tenant2) =>
          this.compareTimestamps(
            tenant1.license?.endTimestamp,
            tenant2.license?.endTimestamp,
            false
          );
      default:
        return (tenant1, tenant2) => {
          if (tenant1.clientName > tenant2.clientName) return 1;
          else if (tenant1.clientName < tenant2.clientName) return -1;
          else
            // If tenant names are the same, then sort DESC by number of machines
            return (
              tenant2.registeredMachines.length -
              tenant1.registeredMachines.length
            );
        };
    }
  }

  // Tenants without a license (or without the relevant date) always sort to the end, in either direction
  private compareTimestamps(
    timestamp1: number | undefined,
    timestamp2: number | undefined,
    ascending: boolean
  ): number {
    if (timestamp1 === undefined && timestamp2 === undefined) return 0;
    if (timestamp1 === undefined) return 1;
    if (timestamp2 === undefined) return -1;
    return ascending ? timestamp1 - timestamp2 : timestamp2 - timestamp1;
  }

  protected updateLicenseStatusOption(status: LicenseStatus | undefined): void {
    this.licenseStatusOption = status;
    if (status) {
      window.localStorage.setItem(
        this.LICENSE_STATUS_FILTER_LOCAL_STORAGE_ITEM_KEY,
        status
      );
    } else {
      window.localStorage.removeItem(
        this.LICENSE_STATUS_FILTER_LOCAL_STORAGE_ITEM_KEY
      );
    }

    this.filterMatchingTenants();
  }

  protected updateSortOption(option: TenantSortOption): void {
    this.sortOption = option;
    window.localStorage.setItem(
      this.SORT_OPTION_LOCAL_STORAGE_ITEM_KEY,
      option
    );

    this.filterMatchingTenants();
  }

  ngOnDestroy(): void {
    this.oaiboxStatusSubscription?.unsubscribe();
    this.tenantsSubscription?.unsubscribe();
  }
}
